/**
 * Readable wording for Horizon activity rows.
 *
 * The backend records a buyer's page views with the Horizon portal path they opened, so a row can
 * read "Viewed /lead/6ac7274bb054efb6f069d668". Those are paths in the buyer's portal, not Remmi
 * routes: they are never turned into links here, only into words an agent can read.
 */

/** One Horizon page: the sentence for a bare "Viewed <path>" row, and the noun used inside a longer title. */
interface HorizonPageText {
  sentence: string;
  noun: string;
}

const UNKNOWN_PAGE: HorizonPageText = { sentence: 'Viewed a page', noun: 'a page' };

/** Keyed by the first segment of the portal path. */
const HORIZON_PAGES: Record<string, HorizonPageText> = {
  '': { sentence: 'Viewed the project home', noun: 'the project home' },
  lead: { sentence: 'Viewed the project home', noun: 'the project home' },
  dashboard: { sentence: 'Viewed the dashboard', noun: 'the dashboard' },
  'project-info': { sentence: 'Viewed Project Info', noun: 'Project Info' },
  'my-property': { sentence: 'Viewed Project Info', noun: 'Project Info' },
  preferences: { sentence: 'Viewed My Preferences', noun: 'My Preferences' },
  'shared-with-me': { sentence: 'Viewed Shared With Me', noun: 'Shared With Me' },
  updates: { sentence: 'Viewed Updates', noun: 'Updates' },
  support: { sentence: 'Viewed Support', noun: 'Support' },
  portfolio: { sentence: 'Viewed their portfolio', noun: 'their portfolio' },
  workspace: { sentence: 'Viewed their workspace', noun: 'their workspace' },
  account: { sentence: 'Viewed their account', noun: 'their account' },
  project: { sentence: 'Explored the project', noun: 'the project' },
  apply: { sentence: 'Viewed the application form', noun: 'the application form' },
};

/** A path token at the start of a word: "/lead/abc", "/project-info?tab=gallery", or a full portal URL. "2/3.pdf" is not one. */
const PATH_TOKEN = /(^|\s)((?:https?:\/\/[^\s/]+)?\/[^\s]*)/g;

/** A title that is only a verb and a path, e.g. "Viewed /support" or just "/support". */
const BARE_PATH_TITLE = /^\s*(?:(?:viewed|visited|opened|page view:?)\s+)?((?:https?:\/\/[^\s/]+)?\/[^\s]*)\s*$/i;

/** "floor-plans" → "Floor Plans". */
function titleCase(value: string): string {
  return value
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

/** A Mongo-style id or a long number is an id, not a page or tab name. */
function isId(segment: string): boolean {
  return /^[0-9a-f]{24}$/i.test(segment) || /^\d+$/.test(segment);
}

function splitPath(path: string): { segments: string[]; query: URLSearchParams } {
  const withoutOrigin = path.replace(/^https?:\/\/[^/]+/i, '');
  const [pathname, search = ''] = withoutOrigin.split('?');
  const segments = pathname
    .split('#')[0]
    .split('/')
    .filter(Boolean)
    .map(segment => decodeURIComponent(segment));
  return { segments, query: new URLSearchParams(search.split('#')[0]) };
}

/** The Project Info tab (and gallery category) a path points at, e.g. " – Gallery (Interiors)". */
function projectInfoDetail(segments: string[], query: URLSearchParams): string {
  const tab = query.get('tab') || (segments[1] && !isId(segments[1]) ? segments[1] : '');
  const category = query.get('category');
  if (!tab) {
    return '';
  }
  return ` – ${titleCase(tab)}${category ? ` (${titleCase(category)})` : ''}`;
}

/** The wording for one Horizon portal path. Unknown pages read "Viewed a page". */
export function describeHorizonPath(path: string): HorizonPageText {
  const { segments, query } = splitPath(path);
  const first = (segments[0] ?? '').toLowerCase();
  const page = HORIZON_PAGES[first];
  if (!page) {
    return UNKNOWN_PAGE;
  }
  if (first === 'updates' && segments[1]) {
    return { sentence: 'Read an update', noun: 'an update' };
  }
  if (first === 'project-info' || first === 'my-property') {
    const detail = projectInfoDetail(segments, query);
    return { sentence: page.sentence + detail, noun: page.noun + detail };
  }
  return page;
}

/** Whether a piece of text is just a Horizon portal path (so it is not worth showing as a subtitle). */
export function isHorizonPath(text: string | null | undefined): boolean {
  return /^\s*(?:https?:\/\/[^\s/]+)?\/[^\s]*\s*$/.test(text ?? '');
}

/**
 * "Viewed /lead/6ac7…" → "Viewed the project home"; "Michael viewed /updates/12" → "Michael viewed an update".
 * A title without a portal path is returned unchanged.
 */
export function humaniseHorizonActivityTitle(title: string | null | undefined): string {
  const text = title ?? '';
  const bare = BARE_PATH_TITLE.exec(text);
  if (bare) {
    return describeHorizonPath(bare[1]).sentence;
  }
  return text.replace(PATH_TOKEN, (_match, lead: string, path: string) => lead + describeHorizonPath(path).noun);
}

/** The fields of a timeline row that decide its wording. */
export interface HorizonActivityTextSource {
  kind?: string | null;
  type?: string | null;
  title?: string | null;
  label?: string | null;
}

/**
 * The text of one timeline row. A row with `label` (contract §4.2: the Horizon page in words, e.g.
 * "Project Info · Floorplans") is worded from it: the backend's title when it already reads in words and names
 * the label, else "Viewed {label}" for a page view and the label alone otherwise. Only a row without `label`
 * falls back to turning a portal path in its title into words.
 */
export function horizonActivityTitle(item: HorizonActivityTextSource | null | undefined): string {
  const label = (item?.label ?? '').trim();
  if (!label) {
    return humaniseHorizonActivityTitle(item?.title);
  }
  const title = (item?.title ?? '').trim();
  if (title && title.includes(label) && !/(^|\s)(?:https?:\/\/[^\s/]+)?\//.test(title)) {
    return title;
  }
  return (item?.kind ?? item?.type) === 'page_view' ? `Viewed ${label}` : label;
}
